import React, { useCallback, useMemo, useRef } from 'react';

import { useIntl } from 'react-intl';
import { graphql, type PreloadedQuery, usePreloadedQuery } from 'react-relay';

import { fg } from '@atlaskit/platform-feature-flags/fg';

import { universalTypeFilterId } from '../../../common/constants/filters';
import type { NounKeys } from '../../../common/constants/nouns';
import { type ProductKeys } from '../../../common/constants/product-keys';
import {
	type ThirdPartyConfigsBootstrap,
	type ThirdPartyConfigsWithAPIResponse,
} from '../../../common/constants/schemas/3p-config';
import { BrowserExtensionBanner } from '../../../common/ui/browser-extension-banner';
import { AppFilters } from '../../../common/ui/filters/app-filter-sidebar/app-filters';
import type { Sections } from '../../../common/ui/filters/app-filter-sidebar/app-filters/dynamic-sections';
import { NounFilters } from '../../../common/ui/filters/app-filter-sidebar/noun-filters';
import { SidebarContainer } from '../../../common/ui/filters/app-filter-sidebar/sidebar-container';
import { SidebarLoading } from '../../../common/ui/filters/app-filter-sidebar/sidebar-loading';
import { GiveFeedback, type GiveFeedbackProps } from '../../../common/ui/give-feedback';
import { useAppFilterState } from '../../../common/ui/right-panel';
import { getUnifiedSearchRightRailEnhancementsExpValue } from '../../../common/utils/experiments';
import { serveFederatedSlackExperiment } from '../../../common/utils/experiments/experiment-utils';
import { useAnalytics, useCommonAndPrivateAttributes } from '../../../controllers/analytics';
import { useFireVisibleProductsToAuthShown } from '../../../controllers/analytics/use-visible-products-to-auth-shown';
import { ErrorBoundary } from '../../../controllers/error-boundary';
import { useSelectFilter } from '../../../controllers/filters/base-select-filter';
import { useIsUserOAuthed, useOAuthHandlers } from '../../../controllers/oauth';
import { useRightPanelRecommendations } from '../../../controllers/recommened3pApps';
import { useSearchActions } from '../../../controllers/store/search';
import type {
	rightPanelAsyncQuery,
	rightPanelAsyncQuery$data,
} from './__generated__/rightPanelAsyncQuery.graphql';
import type {
	rightPanelQuery,
	rightPanelQuery$data,
} from './__generated__/rightPanelQuery.graphql';
import { FireSearchPageFiltersViewedFromRail } from './fire-search-page-filters-viewed-from-rail';
import { messages } from './messages';

export type RightPanelProps = GiveFeedbackProps & {
	data?: rightPanelQuery$data | rightPanelAsyncQuery$data;
	fillContainer?: boolean;
	searchQuery: string;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	availableProducts: ProductKeys[];
	availableNouns: NounKeys[];
	selectedProduct?: ProductKeys;
	selectedDatasource?: ThirdPartyConfigsWithAPIResponse;
	federatedSlackCount?: number | null;
	federatedOutlookMailCount?: number | null;
	federatedGmailCount?: number | null;
	hasMoreGmailResults?: boolean;
	hasMoreOutlookMailResults?: boolean;
	personalisedProductSections?: Map<Sections, ProductKeys[]>;
	setPersonalisedProductSections?: (sections: Map<Sections, ProductKeys[]>) => void;
	productsWithTimeout?: ProductKeys[];
	resultCountsLoading?: boolean;
};

export const RightPanelSkeleton = ({
	fillContainer,
}: Pick<RightPanelProps, 'fillContainer'> = {}): React.JSX.Element => {
	const { formatMessage } = useIntl();
	return (
		<SidebarContainer
			fillContainer={fillContainer}
			ariaLabel={formatMessage(messages.groupLabel)}
			data-testid="app-filter-sidebar-loading"
		>
			<SidebarLoading />
		</SidebarContainer>
	);
};

export const RightPanel = ({
	data,
	fillContainer,
	cloudId,
	searchQuery,
	primaryProduct,
	experimentConfig,
	selectedProducts,
	user,
	appMode,
	thirdPartyConfigs,
	availableProducts,
	availableNouns,
	selectedProduct,
	selectedDatasource,
	federatedSlackCount,
	federatedOutlookMailCount,
	federatedGmailCount,
	hasMoreGmailResults,
	hasMoreOutlookMailResults,
	personalisedProductSections,
	setPersonalisedProductSections,
	productsWithTimeout,
	resultCountsLoading,
}: RightPanelProps): React.JSX.Element => {
	const totalCounts = data?.search?.results?.totalCounts;

	const productResultCountMap = useMemo(() => {
		if (!totalCounts) {
			return {};
		}

		const countMap = totalCounts.reduce(
			(prev, { product, count }) => ({ ...prev, [product]: count }),
			{} as Record<string, number>,
		);

		// the backend returns townsquare-goals and townsquare-projects but the
		// frontend expects goal and project, so we need to map them here.
		if ('townsquare-goal' in countMap) {
			countMap['goal'] = countMap['townsquare-goal'];
			delete countMap['townsquare-goal'];
		}
		if ('townsquare-project' in countMap) {
			countMap['project'] = countMap['townsquare-project'];
			delete countMap['townsquare-project'];
		}

		// Combine passionfruit counts with mercury (Focus product)
		if ('passionfruit' in countMap && fg('platform_passionfruit_search_onboarding_enabled')) {
			countMap['mercury'] = countMap['mercury'] + countMap['passionfruit'];
			delete countMap['passionfruit'];
		}

		// Special handling for Slack: use federatedSlackCount when available
		const isSlackFederatedSearchEnabled = serveFederatedSlackExperiment(
			selectedProduct,
			undefined,
			{ fireExperimentExposure: false },
			thirdPartyConfigs,
		);
		if (
			federatedSlackCount !== null &&
			federatedSlackCount !== undefined &&
			'slack' in countMap &&
			isSlackFederatedSearchEnabled
		) {
			countMap['slack'] = federatedSlackCount;
		}

		// Special handling for Outlook Mail: use federatedOutlookMailCount when available.
		// Outlook Mail uses the same selected-product federated count behavior as Gmail.
		if (federatedOutlookMailCount !== null && federatedOutlookMailCount !== undefined) {
			countMap['outlook-mail'] = federatedOutlookMailCount;
		}

		// Special handling for Gmail: use federatedGmailCount when available.
		// Gmail is a federated connector (isFederated: true) so it is excluded from thirdPartyFilters
		// in the right panel count query — meaning the aggregator never returns a totalCounts entry
		// for Gmail. We therefore set the count directly without checking 'gmail' in countMap.
		if (federatedGmailCount !== null && federatedGmailCount !== undefined) {
			countMap['gmail'] = federatedGmailCount;
		}

		return countMap;
	}, [
		totalCounts,
		federatedSlackCount,
		federatedOutlookMailCount,
		federatedGmailCount,
		selectedProduct,
		thirdPartyConfigs,
	]);

	const { formatMessage } = useIntl();
	const {
		selectProductFilter,
		unselectProductFilter,
		unsafe_setAtlasNouns,
		unsafe_setWebPageType,
	} = useSearchActions();
	const { fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes } = useCommonAndPrivateAttributes();
	const railContainerRef = useRef<HTMLDivElement>(null);
	const [universalTypeFilterData] = useSelectFilter({ filterId: universalTypeFilterId });

	const [filters, { setFilterHidden }] = useAppFilterState();
	const { recommended3pApps } = useRightPanelRecommendations({ userId: user?.id });
	// In the prequery state (aka initial state), there is no data from the query
	const isInitialState = !data;

	const onSelectProductWithAnalytics = useCallback(
		(
			newProduct: ProductKeys | undefined,
			datasource: string | ThirdPartyConfigsWithAPIResponse | undefined,
		) => {
			if (!newProduct && selectedProduct) {
				unselectProductFilter();
				// Note: the `button clicked searchPageRightPanelAppFilter` event is fired by the
				// child filter components (AppFilters / ShowMoreAppsPopup / NounFilters), which
				// have access to richer attributes such as `index` and `isInShowMore`. Firing it
				// again here would result in a duplicate event without those attributes.
				return;
			}

			selectProductFilter(newProduct, datasource);

			// Note: the `button clicked searchPageRightPanelAppFilter` event is fired by the
			// child filter components (AppFilters / ShowMoreAppsPopup / NounFilters), which
			// have access to richer attributes such as `index` and `isInShowMore`. Firing it
			// again here would result in a duplicate event without those attributes.
		},
		[selectedProduct, unselectProductFilter, selectProductFilter],
	);

	const appFilterProps = useMemo(
		() => ({
			searchQuery,
			thirdPartyConfigs,
			availableProducts,
			availableNouns,
			selectedProduct,
			selectedDatasource,
			primaryProduct,
			cloudId,
			filterData: universalTypeFilterData,
			setAtlasNoun: unsafe_setAtlasNouns,
			setWebPageType: unsafe_setWebPageType,
			onSelectProduct: onSelectProductWithAnalytics,
			productResultCountMap: productResultCountMap ?? {},
			sidebarStoredState: filters,
			onFilterHide: (productKey: ProductKeys) => {
				setFilterHidden(productKey, true);
			},
			onFilterShow: (productKey: ProductKeys) => {
				setFilterHidden(productKey, false);
			},
			useOAuthHandlers,
			useIsUserOAuthed,
			ErrorBoundary,
			isInitialState,
			recommended3pApps,
			onVisibleProductsToAuthShown: useFireVisibleProductsToAuthShown,
			personalisedProductSections,
			setPersonalisedProductSections,
			productsWithTimeout,
			resultCountsLoading,
			hasMoreGmailResults,
			hasMoreOutlookMailResults,
		}),
		[
			availableProducts,
			availableNouns,
			selectedProduct,
			selectedDatasource,
			onSelectProductWithAnalytics,
			unsafe_setWebPageType,
			universalTypeFilterData,
			unsafe_setAtlasNouns,
			productResultCountMap,
			filters,
			setFilterHidden,
			thirdPartyConfigs,
			searchQuery,
			primaryProduct,
			cloudId,
			isInitialState,
			recommended3pApps,
			personalisedProductSections,
			setPersonalisedProductSections,
			productsWithTimeout,
			resultCountsLoading,
			hasMoreGmailResults,
			hasMoreOutlookMailResults,
		],
	);

	return (
		<SidebarContainer
			fillContainer={fillContainer}
			ariaLabel={formatMessage(messages.groupLabel)}
			data-testid="app-filter-sidebar"
			containerRef={railContainerRef}
		>
			<BrowserExtensionBanner fireAnalyticsEvent={fireAnalyticsEvent} />
			<ErrorBoundary section="SearchPageAppFilter">
				<AppFilters {...appFilterProps} />
			</ErrorBoundary>
			{/* Hide NounFilters for single-select cohort - nouns are merged into Show More popup */}
			{getUnifiedSearchRightRailEnhancementsExpValue() !== 'single-select' && (
				<NounFilters {...appFilterProps} onSelectProduct={onSelectProductWithAnalytics} />
			)}
			<GiveFeedback
				cloudId={cloudId}
				primaryProduct={primaryProduct}
				experimentConfig={experimentConfig}
				selectedProducts={selectedProducts}
				user={user}
				appMode={appMode}
				isCQL={commonAttributes.isCQL}
			/>
			<FireSearchPageFiltersViewedFromRail
				railRef={railContainerRef}
				thirdPartyConfigs={thirdPartyConfigs}
			/>
		</SidebarContainer>
	);
};

export const RightPanelContainer = ({
	rightPanelQuery,
	...props
}: Omit<RightPanelProps, 'data'> & {
	rightPanelQuery: PreloadedQuery<rightPanelQuery>;
}): React.JSX.Element => {
	const queryData = usePreloadedQuery<rightPanelQuery>(
		graphql`
			query rightPanelQuery(
				$query: String!
				$cloudIdARIs: [String!]!
				$limit: Int!
				$entities: [String!]!
				$queryVersion: Int
				$searchSessionId: String
				$sessionId: String
				$commonFilters: SearchCommonFilter
				$confluenceFilters: SearchConfluenceFilter
				$jiraFilters: SearchJiraFilter
				$assetsFilters: SearchAssetsFilter
				$mercuryFilters: SearchMercuryFilter
				$talentFilters: SearchTalentFilter
				$compassFilters: SearchCompassFilter
				$thirdPartyFilters: SearchThirdPartyFilter
				$sort: [SearchSortInput]
				$sourceProduct: String
				$experimentId: String
				$shadowExperimentId: String
				$experimentLayers: [SearchExperimentLayer]
				$disableQueryReplacement: Boolean
				$experience: String!
				$useCaseId: String
			) {
				search {
					results: search(
						experience: $experience
						useCaseId: $useCaseId
						query: $query
						first: $limit
						sort: $sort
						disableQueryReplacement: $disableQueryReplacement
						filters: {
							entities: $entities
							locations: $cloudIdARIs
							commonFilters: $commonFilters
							confluenceFilters: $confluenceFilters
							jiraFilters: $jiraFilters
							mercuryFilters: $mercuryFilters
							compassFilters: $compassFilters
							thirdPartyFilters: $thirdPartyFilters
							assetsFilters: $assetsFilters
							talentFilters: $talentFilters
						}
						analytics: {
							queryVersion: $queryVersion
							searchSessionId: $searchSessionId
							sourceProduct: $sourceProduct
							sessionId: $sessionId
						}
						experimentContext: {
							experimentId: $experimentId
							shadowExperimentId: $shadowExperimentId
							experimentLayers: $experimentLayers
						}
					) {
						totalCounts {
							product
							count
						}
					}
				}
			}
		`,
		rightPanelQuery,
	);

	return <RightPanel {...props} data={queryData} />;
};

export const RightPanelAsyncContainer = ({
	rightPanelQuery: rightPanelAsyncQueryRef,
	...props
}: Omit<RightPanelProps, 'data'> & {
	rightPanelQuery: PreloadedQuery<rightPanelAsyncQuery>;
}): React.JSX.Element => {
	const queryData = usePreloadedQuery<rightPanelAsyncQuery>(
		graphql`
			query rightPanelAsyncQuery(
				$query: String!
				$cloudIdARIs: [String!]!
				$limit: Int!
				$entities: [String!]!
				$queryVersion: Int
				$searchSessionId: String
				$sessionId: String
				$commonFilters: SearchCommonFilter
				$confluenceFilters: SearchConfluenceFilter
				$jiraFilters: SearchJiraFilter
				$assetsFilters: SearchAssetsFilter
				$mercuryFilters: SearchMercuryFilter
				$talentFilters: SearchTalentFilter
				$compassFilters: SearchCompassFilter
				$thirdPartyFilters: SearchThirdPartyFilter
				$sort: [SearchSortInput]
				$sourceProduct: String
				$experimentId: String
				$shadowExperimentId: String
				$experimentLayers: [SearchExperimentLayer]
				$disableQueryReplacement: Boolean
				$experience: String!
				$useCaseId: String
				$expectResult: Boolean
			) {
				search {
					results: asyncSearch(
						experience: $experience
						useCaseId: $useCaseId
						expectResult: $expectResult
						query: $query
						first: $limit
						sort: $sort
						disableQueryReplacement: $disableQueryReplacement
						filters: {
							entities: $entities
							locations: $cloudIdARIs
							commonFilters: $commonFilters
							confluenceFilters: $confluenceFilters
							jiraFilters: $jiraFilters
							mercuryFilters: $mercuryFilters
							compassFilters: $compassFilters
							thirdPartyFilters: $thirdPartyFilters
							assetsFilters: $assetsFilters
							talentFilters: $talentFilters
						}
						analytics: {
							queryVersion: $queryVersion
							searchSessionId: $searchSessionId
							sourceProduct: $sourceProduct
							sessionId: $sessionId
						}
						experimentContext: {
							experimentId: $experimentId
							shadowExperimentId: $shadowExperimentId
							experimentLayers: $experimentLayers
						}
					) {
						isCachedResult
						totalCounts {
							product
							count
						}
					}
				}
			}
		`,
		rightPanelAsyncQueryRef,
	);

	void queryData.search?.results?.isCachedResult;

	return <RightPanel {...props} data={queryData} />;
};

/**
 * Resolves which right panel query container to use — always uses RightPanelAsyncContainer.
 * A single union-typed ref is sufficient.
 */
export const RightPanelContainerResolver = ({
	rightPanelQueryRef,
	...props
}: Omit<RightPanelProps, 'data'> & {
	rightPanelQueryRef: PreloadedQuery<rightPanelQuery> | PreloadedQuery<rightPanelAsyncQuery>;
}): React.JSX.Element => {
	return (
		<RightPanelAsyncContainer
			rightPanelQuery={rightPanelQueryRef as PreloadedQuery<rightPanelAsyncQuery>}
			{...props}
		/>
	);
};
